import { useContext, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { lineBeingTyped } from "./previewFocus";
import { TEXT_SLOTS } from "./registry";
import { SlotContext } from "./SlotContext";
import { cutLine, fitTypedLine, slotDefault } from "./slots";

// `plaintext-only` keeps formatting (bold, pasted styles) out of the line and
// lets Enter insert a plain line break. Browsers that don't know it throw when
// it is set, and fall back to an ordinary editable element.
const EDITABLE_MODE = (() => {
  try {
    const probe = document.createElement("div");
    probe.contentEditable = "plaintext-only";
    return probe.contentEditable === "plaintext-only" ? "plaintext-only" : "true";
  } catch {
    return "true";
  }
})();

// Spaces typed at the edge of a line arrive as no-break spaces.
const NO_BREAK_SPACE = String.fromCharCode(0xa0);

/**
 * The text under a node, line breaks as "\n" whether the browser typed them
 * as text or as <br>/<div>. textContent, not innerText: innerText applies CSS
 * text-transform, and several slots are set in capitals — the couple's own
 * casing is what must reach the editor.
 */
function textUnder(root) {
  let text = "";
  const walk = (node) => {
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.nodeValue;
      } else if (child.nodeName === "BR") {
        text += "\n";
      } else {
        if (text && !text.endsWith("\n") && /^(DIV|P)$/.test(child.nodeName)) text += "\n";
        walk(child);
      }
    }
  };
  walk(root);
  return text;
}

/** The text of an edited line (see textUnder). */
function readLine(el, multiline) {
  let text = textUnder(el);
  // A browser keeps an empty or newline-ended line open with a trailing <br>.
  if (el.lastChild?.nodeName === "BR" && text.endsWith("\n")) text = text.slice(0, -1);
  text = text.replace(/\r\n?/g, "\n").replaceAll(NO_BREAK_SPACE, " ");
  if (!multiline) text = text.replace(/\n/g, " ");
  return text.trim() ? text : "";
}

/** How many more characters fit, counting the selection a keystroke replaces. */
function roomLeft(el, max, multiline) {
  const selection = window.getSelection();
  const selected =
    selection && selection.rangeCount && el.contains(selection.anchorNode)
      ? selection.toString().length
      : 0;
  return max - (readLine(el, multiline).length - selected);
}

/** Where the caret is in the line readLine reads, or null when it isn't in the line. */
function caretOffset(el) {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return null;
  const caret = selection.getRangeAt(0);
  if (!el.contains(caret.startContainer)) return null;
  const before = document.createRange();
  before.selectNodeContents(el);
  before.setEnd(caret.startContainer, caret.startOffset);
  return textUnder(before.cloneContents()).length;
}

/** Puts `line` on the page, with the caret at `offset` — at the end when null. */
function showLine(el, line, offset) {
  el.textContent = line;
  const range = document.createRange();
  if (offset === null || !el.firstChild) {
    range.selectNodeContents(el);
    range.collapse(false);
  } else {
    range.setStart(el.firstChild, Math.min(offset, line.length));
    range.collapse(true);
  }
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * One line of the template that a couple may rewrite. Shows their line, else
 * the template's own (the page puts both in SlotContext); multiline slots keep
 * the line breaks the couple wrote.
 *
 * In the editor's preview the line can be typed on in place — see EditableLine.
 */
const SlotText = ({ slot, as = "p", style, ...rest }) => {
  const slots = useContext(SlotContext);
  const { t } = useTranslation();
  const def = TEXT_SLOTS[slot];
  const text = slots?.texts?.[slot] ?? slotDefault(t, slot);
  const multiline = !!def?.multiline;

  if (slots?.editable && def) {
    return (
      <EditableLine
        slot={slot}
        as={as}
        text={text}
        max={def.max}
        multiline={multiline}
        onTextInput={slots.onTextInput}
        style={multiline ? { ...style, whiteSpace: "pre-line" } : style}
        {...rest}
      />
    );
  }

  // Only a line that has breaks needs pre-line; the template's own lines have
  // none, so guests' markup stays exactly as it always was.
  const Tag = as;
  const keepsBreaks = multiline && text.includes("\n");
  return (
    <Tag style={keepsBreaks ? { ...style, whiteSpace: "pre-line" } : style} {...rest}>
      {text}
    </Tag>
  );
};

/**
 * A line the couple types on inside the preview. While they type, its text
 * belongs to the browser: React never writes it (re-rendering a focused line
 * would throw the caret back to the start), and the line is synced from the
 * editor's copy only when it isn't being typed on. The editor echoes every
 * keystroke back in its next `render`, so on blur the two already agree — and
 * where they don't (a line cleared back to the template's), the editor wins.
 */
const EditableLine = ({ slot, as, text, max, multiline, onTextInput, ...rest }) => {
  const ref = useRef(null);
  // The line and caret as they were when a composition began — what the line
  // goes back to when the composed word doesn't fit.
  const compositionRef = useRef(null);
  const Tag = as;

  // Once the couple is back in the editor the line follows the panel again,
  // so it is current when they click on it to carry on typing.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && lineBeingTyped() !== el && el.textContent !== text) {
      el.textContent = text;
    }
  }, [text]);

  // React's onBeforeInput is a keypress polyfill without `inputType`; the
  // native event is the one that can refuse a line break or an overflow.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onBeforeInput = (e) => {
      if (e.isComposing) return;
      const type = e.inputType || "";
      if (type.startsWith("format") || type === "insertFromDrop") {
        e.preventDefault();
        return;
      }
      let inserted;
      if (type === "insertParagraph" || type === "insertLineBreak") {
        if (!multiline) {
          e.preventDefault();
          return;
        }
        inserted = "\n";
      } else if (type.startsWith("insert")) {
        inserted = e.data ?? e.dataTransfer?.getData("text/plain") ?? "";
        if (!multiline && /[\r\n]/.test(inserted)) {
          e.preventDefault();
          return;
        }
      } else {
        return;
      }
      if (inserted.length > roomLeft(el, max, multiline)) e.preventDefault();
    };
    el.addEventListener("beforeinput", onBeforeInput);
    return () => el.removeEventListener("beforeinput", onBeforeInput);
  }, [max, multiline]);

  // What reaches the editor is never longer than the slot allows. A word an
  // IME composed past the max is refused: what is posted stays the line from
  // before it, and the page goes back to that line once the composition is
  // over (see fitTypedLine).
  const postLine = (el, composing) => {
    const composition = compositionRef.current;
    const { value, fix } = fitTypedLine(readLine(el, multiline), max, {
      composedFrom: composition?.line ?? null,
      composing,
    });
    if (fix === "restore") showLine(el, value, composition.caret);
    else if (fix === "cut") showLine(el, value, null);
    onTextInput?.(slot, value);
  };

  const handleInput = (e) => postLine(e.currentTarget, e.nativeEvent.isComposing);

  const handleCompositionStart = (e) => {
    const el = e.currentTarget;
    compositionRef.current = { line: readLine(el, multiline), caret: caretOffset(el) };
  };

  // Chrome sends a composition's last `input` while it is still composing and
  // no input after it, so a word that overfilled the line is refused here. A
  // line that fits was already posted by that input. Firefox sends its last
  // input after this one, so the line from before the composition is kept
  // until the browser is done with it.
  const handleCompositionEnd = (e) => {
    const el = e.currentTarget;
    if (readLine(el, multiline).length > max) postLine(el, false);
    const ended = compositionRef.current;
    setTimeout(() => {
      if (compositionRef.current === ended) compositionRef.current = null;
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !multiline) e.preventDefault();
    if (e.key === "Escape") e.currentTarget.blur();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    let pasted = (e.clipboardData?.getData("text/plain") ?? "").replace(/\r\n?/g, "\n");
    if (!multiline) pasted = pasted.replace(/\s*\n\s*/g, " ");
    pasted = cutLine(pasted, roomLeft(e.currentTarget, max, multiline));
    // insertText keeps the browser's undo history, unlike editing the DOM.
    if (pasted) document.execCommand("insertText", false, pasted);
  };

  const handleBlur = (e) => {
    if (e.currentTarget.textContent !== text) e.currentTarget.textContent = text;
  };

  return (
    <Tag
      ref={ref}
      {...rest}
      contentEditable={EDITABLE_MODE}
      data-invitation-slot={slot}
      onInput={handleInput}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onDrop={(e) => e.preventDefault()}
      onBlur={handleBlur}
    />
  );
};

export default SlotText;
